import {
	FormControl,
	Grid,
	OutlinedInput,
	Typography,
	InputAdornment,
	debounce,
	Box,
} from '@mui/material';

import { useEffect, useState } from 'react';

import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import { Loader } from 'common/components/Loader/Loader';
import { TOAST_SEVERITY } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	APP_GENERAL_ERROR_MSG,
	APP_GENERIC_BAD_REQUEST_MSG,
} from 'common/constants/TextContent';

import {
	deleteChartHistory,
	getChartHistory,
	getChartSearchHistory,
	updateChatHistoryName,
} from '../api';
import { IChatHistory, IGroupChatHistory } from '../api/type-def';

import { getGroupChatHistory } from './ChatHistoryUtil';
import GroupChatSession from './GroupChatSession';

import style from './ChatHistory.module.scss';

interface IChatHistoryProps {
	handleSelectChatHistory: (id: string, knowledgeBase: string) => void;
	handleToastChange: (message: string, severity: string) => void;
	handleChatHistoryDeleted?: (isHistoryDeleted: boolean) => void;
}

const { OK, BAD_REQUEST, NOT_FOUND } = HTTP_STATUS_CODES;
const { ERROR, SUCCESS } = TOAST_SEVERITY;

const ChatHistory = ({
	handleSelectChatHistory,
	handleToastChange,
	handleChatHistoryDeleted,
}: IChatHistoryProps) => {
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [editChatIdClick, setEditChatIdClick] = useState<string>('');
	const [chatGroupData, setChatGroupData] = useState<IGroupChatHistory[]>([]);
	const [searchText, setSearchText] = useState<string>('');

	const getSearchHistoryDetails = async (searchText: string) => {
		setIsLoading(true);
		const resp = await getChartSearchHistory(searchText);
		setIsLoading(false);
		onChatHistoryDataFetch(resp);
	};

	const changeSearchInput = (event: any) => {
		const text = event.target.value;
		setSearchText(text);
		if (text.trim() !== '' || text.length !== 0) {
			getSearchHistoryDetails(text);
		} else {
			getHistoryDetails();
		}
	};
	const debouncedSearchHandler = debounce(changeSearchInput, 500);

	const getHistoryDetails = async () => {
		setIsLoading(true);
		const resp = await getChartHistory();
		setIsLoading(false);
		onChatHistoryDataFetch(resp);
	};

	const onChatHistoryDataFetch = (resp: any) => {
		if (resp.responseBody && resp.statusCode === OK) {
			const data = resp.responseBody as IChatHistory[];
			const groupData = getGroupChatHistory(data);
			setChatGroupData(groupData);
		} else {
			displayErroNotificaition(resp);
		}
	};

	const displayErroNotificaition = (resp: any) => {
		if (resp.statusCode === BAD_REQUEST) {
			handleToastChange(APP_GENERIC_BAD_REQUEST_MSG, ERROR);
		} else if (resp.statusCode === NOT_FOUND) {
			handleToastChange('No recent search history found.', ERROR);
		} else {
			handleToastChange(APP_GENERAL_ERROR_MSG, ERROR);
		}
	};

	const handleDeleteChatHistory = async (sessionId: string) => {
		const resp = await deleteChartHistory(sessionId);
		if (resp.responseBody && resp.statusCode === OK) {
			handleToastChange('Chat history deleted successfully.', SUCCESS);
			if (handleChatHistoryDeleted) {
				handleChatHistoryDeleted(true);
			}
			getHistoryDetails();
		} else {
			displayErroNotificaition(resp);
		}
	};

	const updateChatName = (chartId: string, chartName: string) => {
		if (chartName && chartId) {
			patchChatName(chartId, chartName);
		}
	};
	const patchChatName = async (chartId: string, chartName: string) => {
		const resp = await updateChatHistoryName(chartId, chartName);
		setEditChatIdClick('');
		if (resp.responseBody && resp.statusCode === OK) {
			handleToastChange('Chat history name updated successfully.', SUCCESS);
			getHistoryDetails();
		} else {
			displayErroNotificaition(resp);
		}
	};

	useEffect(() => {
		getHistoryDetails();
	}, []);

	return (
		<>
			<Box className={style['chat-search-grid']}>
				<FormControl
					variant="standard"
					className={style['chat-search-input']}
					data-testid="form-control-id"
				>
					<OutlinedInput
						className={`${style['detail-summary-bg']}`}
						defaultValue={searchText}
						onChange={debouncedSearchHandler}
						id="input-with-icon-adornment"
						placeholder="Search"
						startAdornment={
							<InputAdornment position="start">
								<SearchOutlined className={style['input-search-icon']} />
							</InputAdornment>
						}
						inputProps={{ 'data-testid': 'chat-elastic-search' }}
					/>
				</FormControl>
			</Box>
			<Grid item sm={12} className={style['chat-history-layer']}>
				{isLoading && <Loader show={isLoading} />}
				{!isLoading && (
					<div>
						{chatGroupData.length > 0 ? (
							chatGroupData.map((group: IGroupChatHistory) => (
								<Grid container key={`chatHistory-${group.groupName}`}>
									<Grid
										item
										sm={12}
										className={style['chat-group-name']}
										data-testid="form-group-name"
									>
										{group.groupName}
									</Grid>
									<Grid item sm={12} pl={1}>
										{group.data.map((ele) => (
											<GroupChatSession
												key={ele.id}
												data={ele}
												handleDeleteChatHistory={handleDeleteChatHistory}
												handleSelectChatHistory={handleSelectChatHistory}
												updateChatHistoryName={updateChatName}
												editClick={editChatIdClick}
												setEditClick={setEditChatIdClick}
											/>
										))}
									</Grid>
								</Grid>
							))
						) : (
							<Typography data-testid="form-group-name-no-record">
								{' '}
								No data found{' '}
							</Typography>
						)}
					</div>
				)}
			</Grid>
		</>
	);
};

export default ChatHistory;
